/**
 * The brand (document organisation) of a workspace: Keystone Glazing, Keystone South UK, …
 *
 * Stored on projects.organisation_id. New workspaces default from the customer's directory
 * organisation; the workspace's quotes, invoices, numbering and emails follow it.
 */
import { directSupabase } from '../../lib/supabaseClient';
import {
  brandByDirectoryId,
  brandByName,
  defaultBrand,
  organisationSettings,
  type Brand,
} from '../../services/organisationSettings';

/** The brand a new workspace should start with for this customer. */
export function brandForCustomer(
  brands: Brand[],
  customer?: { organization_id?: number | null; organization_name?: string | null; reference?: string | null; company?: string | null } | null,
): Brand | null {
  const directoryId = customer?.organization_id ?? (customer?.reference ? Number(customer.reference) : null);
  const byId = Number.isFinite(directoryId as number) ? brandByDirectoryId(brands, directoryId as number) : null;
  const byName = brandByName(brands, customer?.organization_name || customer?.company || null);
  const match = byId || byName;
  // Retired brands (not selectable) hand new work to the default brand
  if (match && match.isSelectable) return match;
  return defaultBrand(brands);
}

/** The workspace's stored brand id, or null if none (or the column is not there yet). */
export async function getWorkspaceBrandId(projectId: string): Promise<string | null> {
  const { data, error } = await (directSupabase as any)
    .from('projects')
    .select('organisation_id')
    .eq('id', projectId)
    .maybeSingle();
  if (error) return null;
  return data?.organisation_id ? String(data.organisation_id) : null;
}

/** The brand to issue a workspace's documents under: its own, else the default brand. */
export async function resolveWorkspaceBrandId(projectId?: string | null): Promise<string> {
  const stored = projectId ? await getWorkspaceBrandId(projectId) : null;
  if (stored) return stored;
  const fallback = await organisationSettings.defaultBrand();
  if (!fallback) throw new Error('No organisation is available for this workspace.');
  return fallback.id;
}

/** Saves the brand on a workspace (used when it is created). */
export async function saveWorkspaceBrand(projectId: string, organisationId: string): Promise<void> {
  const { error } = await (directSupabase as any)
    .from('projects')
    .update({ organisation_id: organisationId })
    .eq('id', projectId);
  if (error && !/organisation_id|column|schema cache/i.test(error.message)) throw new Error(error.message);
}
